"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DVCClient = void 0;
const Variable_1 = require("./Variable");
const Request_1 = require("./Request");
const CacheStore_1 = __importDefault(require("./CacheStore"));
const DefaultStorage_1 = __importDefault(require("./DefaultStorage"));
const User_1 = require("./User");
const EventQueue_1 = require("./EventQueue");
const utils_1 = require("./utils");
const EventEmitter_1 = require("./EventEmitter");
const types_1 = require("@devcycle/types");
const ConfigRequestConsolidator_1 = require("./ConfigRequestConsolidator");
const logger_1 = require("./logger");
const StreamingConnection_1 = require("./StreamingConnection");
class DVCClient {
    constructor(sdkKey, user, options = {}) {
        this.userSaved = false;
        this._closing = false;
        this.isConfigCached = false;
        this.logger =
            options.logger || (0, logger_1.dvcDefaultLogger)({ level: options.logLevel });
        this.store = new CacheStore_1.default(options.storage || new DefaultStorage_1.default(), this.logger);
        this.options = options;
        this.sdkKey = sdkKey;
        this.variableDefaultMap = {};
        this.eventQueue = new EventQueue_1.EventQueue(sdkKey, this, options === null || options === void 0 ? void 0 : options.eventFlushIntervalMS);
        this.eventEmitter = new EventEmitter_1.EventEmitter();
        this.registerVisibilityChangeHandler();
        this.onInitialized = this.store
            .loadAnonUserId()
            .then((storedAnonymousId) => {
            this.user = new User_1.DVCPopulatedUser(user, options, undefined, storedAnonymousId);
            this.requestConsolidator = new ConfigRequestConsolidator_1.ConfigRequestConsolidator((user, extraParams) => (0, Request_1.getConfigJson)(this.sdkKey, user, this.logger, this.options, extraParams), (config, user) => this.handleConfigReceived(config, user, Date.now()), this.user);
            if (!this.options.disableConfigCache) {
                return this.getConfigCache();
            }
            else {
                this.logger.info('Skipping config cache');
                return;
            }
        })
            .then(() => {
            return this.initializeClient();
        })
            .then(() => {
            this.logger.info('Client initialized');
            return this;
        });
        if (!(options === null || options === void 0 ? void 0 : options.reactNative) && typeof window !== 'undefined') {
            this.windowMessageHandler = (event) => {
                const message = event.data;
                if ((message === null || message === void 0 ? void 0 : message.type) === 'DVC.optIn.saved') {
                    this.refetchConfig(false);
                }
            };
            window.addEventListener('message', this.windowMessageHandler);
        }
    }
    onClientInitialized(onInitialized) {
        if (onInitialized && typeof onInitialized === 'function') {
            this.onInitialized
                .then(() => onInitialized(null, this))
                .catch((err) => onInitialized(err));
            return;
        }
        return this.onInitialized;
    }
    variable(key, defaultValue) {
        var _a, _b, _c, _d;
        if (defaultValue === undefined || defaultValue === null) {
            throw new Error('Default value is a required param');
        }
        // this will throw if type is invalid
        const type = (0, types_1.getVariableTypeFromValue)(defaultValue, key, this.logger, true);
        const defaultValueKey = typeof defaultValue === 'string'
            ? defaultValue
            : JSON.stringify(defaultValue);
        let variable;
        if (this.variableDefaultMap[key] &&
            this.variableDefaultMap[key][defaultValueKey]) {
            variable = this.variableDefaultMap[key][defaultValueKey];
        }
        else {
            const configVariable = (_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.variables) === null || _b === void 0 ? void 0 : _b[key];
            const data = {
                key,
                defaultValue,
            };
            if (configVariable) {
                if (configVariable.type === type) {
                    data.value = configVariable.value;
                    data.evalReason = configVariable.evalReason;
                }
                else {
                    this.logger.warn(`Type mismatch for variable ${key}. Expected ${type}, got ${configVariable.type}`);
                }
            }
            variable = new Variable_1.DVCVariable(data);
            this.variableDefaultMap[key] = {
                [defaultValueKey]: variable,
                ...this.variableDefaultMap[key],
            };
        }
        try {
            const variableFromConfig = (_d = (_c = this.config) === null || _c === void 0 ? void 0 : _c.variables) === null || _d === void 0 ? void 0 : _d[key];
            this.eventQueue.queueAggregateEvent({
                type: variable.isDefaulted
                    ? EventQueue_1.EventTypes.variableDefaulted
                    : EventQueue_1.EventTypes.variableEvaluated,
                target: variable.key,
                metaData: {
                    value: variable.value,
                    type: (0, types_1.getVariableTypeFromValue)(variable.defaultValue, variable.key, this.logger),
                    _variable: variableFromConfig === null || variableFromConfig === void 0 ? void 0 : variableFromConfig._id,
                },
            });
        }
        catch (e) {
            this.eventEmitter.emitError(e);
            this.logger.warn(`Error with queueing aggregate events ${e}`);
        }
        this.eventEmitter.emitVariableEvaluated(variable);
        return variable;
    }
    identifyUser(user, callback) {
        const promise = new Promise((resolve, reject) => {
            this.eventQueue.flushEvents();
            let updatedUser;
            this.onInitialized
                .then(() => {
                return this.store.loadAnonUserId();
            })
                .then((storedAnonymousId) => {
                if (user.user_id === this.user.user_id) {
                    updatedUser = this.user.updateUser(user, this.options);
                }
                else {
                    updatedUser = new User_1.DVCPopulatedUser(user, this.options, undefined, storedAnonymousId);
                }
                return this.requestConsolidator.queue(updatedUser);
            })
                .then((config) => {
                if (user.isAnonymous || !user.user_id) {
                    this.store.saveAnonUserId(updatedUser.user_id);
                }
                resolve(config.variables || {});
            })
                .catch((err) => {
                this.eventEmitter.emitError(err);
                reject(err);
            });
        });
        if (callback && typeof callback == 'function') {
            promise
                .then((variables) => callback(null, variables))
                .catch((err) => callback(err, null));
            return;
        }
        return promise;
    }
    resetUser(callback) {
        let oldAnonymousId;
        const anonUser = new User_1.DVCPopulatedUser({ isAnonymous: true }, this.options);
        const promise = new Promise((resolve, reject) => {
            this.eventQueue.flushEvents();
            this.onInitialized
                .then(() => this.store.loadAnonUserId())
                .then((cachedAnonId) => {
                this.store.removeAnonUserId();
                oldAnonymousId = cachedAnonId;
                return;
            })
                .then(() => this.requestConsolidator.queue(anonUser))
                .then((config) => {
                this.store.saveAnonUserId(anonUser.user_id);
                resolve(config.variables || {});
            })
                .catch((e) => {
                this.eventEmitter.emitError(e);
                if (oldAnonymousId) {
                    this.store.saveAnonUserId(oldAnonymousId);
                }
                reject(e);
            });
        });
        if (callback && typeof callback == 'function') {
            promise
                .then((variables) => callback(null, variables))
                .catch((err) => callback(err, null));
            return;
        }
        return promise;
    }
    allFeatures() {
        var _a;
        return ((_a = this.config) === null || _a === void 0 ? void 0 : _a.features) || {};
    }
    allVariables() {
        var _a;
        return ((_a = this.config) === null || _a === void 0 ? void 0 : _a.variables) || {};
    }
    subscribe(key, handler) {
        this.eventEmitter.subscribe(key, handler);
    }
    unsubscribe(key, handler) {
        this.eventEmitter.unsubscribe(key, handler);
    }
    track(event) {
        if (this._closing) {
            this.logger.error('Client is closing, cannot track new events.');
            return;
        }
        (0, utils_1.checkParamDefined)('type', event.type);
        this.onInitialized.then(() => {
            this.eventQueue.queueEvent(event);
        });
    }
    flushEvents(callback) {
        return this.eventQueue.flushEvents().then(() => callback === null || callback === void 0 ? void 0 : callback());
    }
    async close() {
        var _a;
        this.logger.debug('Closing client');
        this._closing = true;
        if (document && this.pageVisibilityHandler) {
            document.removeEventListener('visibilitychange', this.pageVisibilityHandler);
        }
        if (this.windowMessageHandler) {
            window.removeEventListener('message', this.windowMessageHandler);
        }
        (_a = this.streamingConnection) === null || _a === void 0 ? void 0 : _a.close();
        await this.eventQueue.close();
    }
    get closing() {
        return this._closing;
    }
    async refetchConfig(sse, lastModified, etag) {
        await this.onInitialized;
        await this.requestConsolidator.queue(null, { sse, lastModified, etag });
    }
    handleConfigReceived(config, user, dateFetched) {
        var _a;
        const oldConfig = this.config;
        this.config = config;
        this.store.saveConfig(config, user, dateFetched);
        this.isConfigCached = false;
        if (this.user != user || !this.userSaved) {
            this.user = user;
            this.store.saveUser(user);
            if (!this.user.isAnonymous &&
                checkIfEdgeEnabled(config, this.logger, (_a = this.options) === null || _a === void 0 ? void 0 : _a.enableEdgeDB, true)) {
                (0, Request_1.saveEntity)(this.user, this.sdkKey, this.logger, this.options).then((res) => this.logger.info(`Saved response entity! ${res}`));
            }
            this.userSaved = true;
        }
        const oldFeatures = (oldConfig === null || oldConfig === void 0 ? void 0 : oldConfig.features) || {};
        const oldVariables = (oldConfig === null || oldConfig === void 0 ? void 0 : oldConfig.variables) || {};
        this.eventEmitter.emitFeatureUpdates(oldFeatures, config.features);
        this.eventEmitter.emitVariableUpdates(oldVariables, config.variables, this.variableDefaultMap);
        if (!oldConfig || oldConfig.etag !== this.config.etag) {
            this.eventEmitter.emitConfigUpdate(config.variables);
        }
    }
    onSSEMessage(message) {
        var _a, _b;
        try {
            const parsedMessage = JSON.parse(message);
            const messageData = JSON.parse(parsedMessage.data);
            if (!messageData) {
                return;
            }
            if (!messageData.type || messageData.type === 'refetchConfig') {
                if (!((_a = this.config) === null || _a === void 0 ? void 0 : _a.etag) ||
                    messageData.etag !== ((_b = this.config) === null || _b === void 0 ? void 0 : _b.etag)) {
                    this.refetchConfig(true, messageData.lastModified, messageData.etag).catch((e) => {
                        this.logger.warn(`Failed to refetch config ${e}`);
                    });
                }
            }
        }
        catch (e) {
            this.logger.warn(`Streaming Connection: Unparseable message ${e}`);
        }
    }
    registerVisibilityChangeHandler() {
        var _a, _b, _c;
        if (typeof document === 'undefined') {
            return;
        }
        const inactivityDelay = ((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.sse) === null || _b === void 0 ? void 0 : _b.inactivityDelay) || 120000;
        this.pageVisibilityHandler = () => {
            var _a, _b, _c;
            if (!((_a = this.config) === null || _a === void 0 ? void 0 : _a.sse)) {
                return;
            }
            else if (document.visibilityState === 'visible') {
                if (!((_b = this.streamingConnection) === null || _b === void 0 ? void 0 : _b.isConnected())) {
                    this.logger.debug('Page became visible, refetching config');
                    this.refetchConfig(false).catch((e) => {
                        this.logger.warn(`Failed to refetch config ${e}`);
                    });
                    (_c = this.streamingConnection) === null || _c === void 0 ? void 0 : _c.reopen();
                }
                window === null || window === void 0 ? void 0 : window.clearTimeout(this.inactivityHandlerId);
            }
            else {
                window === null || window === void 0 ? void 0 : window.clearTimeout(this.inactivityHandlerId);
                this.inactivityHandlerId = window === null || window === void 0 ? void 0 : window.setTimeout(() => {
                    var _a;
                    this.logger.debug('Page is not visible, closing streaming connection');
                    (_a = this.streamingConnection) === null || _a === void 0 ? void 0 : _a.close();
                }, inactivityDelay);
            }
        };
        (_c = document.addEventListener) === null || _c === void 0 ? void 0 : _c.call(document, 'visibilitychange', this.pageVisibilityHandler);
    }
    async getConfigCache() {
        const cachedConfig = await this.store.loadConfig(this.user, this.options.configCacheTTL);
        if (cachedConfig) {
            this.config = cachedConfig;
            this.isConfigCached = true;
            this.eventEmitter.emitFeatureUpdates({}, cachedConfig.features);
            this.eventEmitter.emitVariableUpdates({}, cachedConfig.variables, this.variableDefaultMap);
            this.logger.debug('Initialized with a cached config');
        }
    }
    async initializeClient() {
        return this.requestConsolidator
            .queue(this.user)
            .then(() => {
            var _a, _b;
            this.eventEmitter.emitInitialized(true);
            if (this.user.isAnonymous) {
                this.store.saveAnonUserId(this.user.user_id);
            }
            else {
                this.store.removeAnonUserId();
            }
            if ((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.sse) === null || _b === void 0 ? void 0 : _b.url) {
                if (!this.options.disableRealtimeUpdates) {
                    this.streamingConnection = new StreamingConnection_1.StreamingConnection(this.config.sse.url, this.onSSEMessage.bind(this), this.logger);
                }
                else {
                    this.logger.info('Disabling Realtime Updates based on Initialization parameter');
                }
            }
            return this;
        })
            .catch((err) => {
            this.eventEmitter.emitInitialized(false);
            this.eventEmitter.emitError(err);
            return this;
        });
    }
}
exports.DVCClient = DVCClient;
const checkIfEdgeEnabled = (config, logger, enableEdgeDB, logWarning = false) => {
    var _a, _b;
    if ((_b = (_a = config.project.settings) === null || _a === void 0 ? void 0 : _a.edgeDB) === null || _b === void 0 ? void 0 : _b.enabled) {
        return !!enableEdgeDB;
    }
    else {
        if (enableEdgeDB && logWarning) {
            logger.warn('EdgeDB is not enabled for this project. Only using local user data.');
        }
        return false;
    }
};
//# sourceMappingURL=Client.js.map